import { Dispatch, SetStateAction } from "react";

import CalculationDropDownField from "@/components/Calculator/Components/CalculationDropDownField/calculationDropDownField.component";
import CalculationField from "@/components/Calculator/Components/CalculationField/calculationField.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { DamImage } from "@/utils/dam.utils";

import { CalculatorImage } from "../../Components/CalculatorImage/calculatorImage.component";
import {
    OndulineColor,
    ProductLine,
    ProductLineType,
    Sides
} from "../corrugatedSheetCalculator.types";
import { CorrugatedSheetsCalculationConfiguration } from "../corrugatedSheetsCalculation.config";

type CalculationComponentProps = {
    setHeight: Dispatch<SetStateAction<string>>;
    setWidth: Dispatch<SetStateAction<string>>;
    setLength: Dispatch<SetStateAction<string>>;
    setProductLine: Dispatch<SetStateAction<ProductLineType>>;
    setIsTwoSidedRoof: Dispatch<SetStateAction<boolean>>;
    setColor: Dispatch<SetStateAction<OndulineColor | undefined>>;
    height: string;
    width: string;
    length: string;
    productLine: ProductLineType;
    isTwoSidedRoof: boolean;
    color?: OndulineColor;
};

const CalculationComponent = ({
    setHeight,
    setLength,
    setWidth,
    setProductLine,
    setIsTwoSidedRoof,
    setColor,
    height,
    length,
    width,
    productLine,
    isTwoSidedRoof,
    color
}: CalculationComponentProps) => {
    const { t } = useGranite();

    return (
        <div className={"mt-4"}>
            <Row>
                <Col md={6}>
                    <h3 className="d-sm-block">
                        {t(
                            "calculator.corrugatedSheetCalculator.corrugatedSheetMeasurements"
                        )}
                    </h3>
                    <CalculationField
                        classNames="pl-0"
                        md={6}
                        name={"corrugatedSheetHeight"}
                        dimension={"height"}
                        unit={"cm"}
                        setCalculationValue={(_: string, value: string) =>
                            setHeight(value)
                        }
                        value={height}
                    />
                    <CalculationField
                        classNames="pl-0"
                        md={6}
                        name={"corrugatedSheetWidth"}
                        dimension={"width"}
                        unit={"cm"}
                        setCalculationValue={(_: string, value: string) =>
                            setWidth(value)
                        }
                        value={width}
                    />
                    <CalculationField
                        classNames="pl-0"
                        md={6}
                        name={"corrugatedSheetLength"}
                        dimension={"length"}
                        unit={"cm"}
                        setCalculationValue={(_: string, value: string) =>
                            setLength(value)
                        }
                        value={length}
                    />
                </Col>
                <Col md={6}>
                    <CalculatorImage
                        size="large"
                        src={DamImage.calculator.corrugatedSheets()}
                        alt={"calculator-corrugated-sheets.png"}
                    />
                </Col>
            </Row>
            <Row>
                <CalculationDropDownField
                    md={6}
                    values={[
                        {
                            value: Sides.SingleSided,
                            label: t(
                                `calculator.corrugatedSheetCalculator.singleSided`
                            )
                        },
                        {
                            value: Sides.DoubleSided,
                            label: t(
                                `calculator.corrugatedSheetCalculator.doubleSided`
                            )
                        }
                    ]}
                    name={"corrugatedSheetSides"}
                    dimension={"corrugatedSheets.sides"}
                    setCalculationValue={(_: string, value: string) =>
                        setIsTwoSidedRoof(value === Sides.DoubleSided)
                    }
                    value={
                        isTwoSidedRoof ? Sides.DoubleSided : Sides.SingleSided
                    }
                />
            </Row>
            <Row>
                <CalculationDropDownField
                    md={6}
                    values={Object.keys(ProductLine).map((value) => ({
                        value,
                        label: t(
                            `calculator.corrugatedSheetCalculator.productLine.${value}`
                        )
                    }))}
                    name={"corrugatedSheetProductLine"}
                    dimension={"corrugatedSheets.productLine"}
                    setCalculationValue={(_: string, value: string) =>
                        setProductLine(value as ProductLineType)
                    }
                    value={productLine}
                />
            </Row>
            <Row>
                <CalculationDropDownField
                    md={6}
                    values={
                        CorrugatedSheetsCalculationConfiguration[
                            productLine
                        ].sheets?.map((value) => ({
                            value: value.color.toString(),
                            label: t(
                                `calculator.corrugatedSheetCalculator.productLine.color.${value.color}`
                            )
                        })) || []
                    }
                    name={"corrugatedSheetProductLineColor"}
                    dimension={"corrugatedSheets.productLineColor"}
                    setCalculationValue={(_: string, value: string) =>
                        setColor(value as unknown as OndulineColor)
                    }
                    value={color}
                />
            </Row>
        </div>
    );
};

export default CalculationComponent;
